// Desk-phone calls on the lead page (CU-869fm5hj3). The ONLY place that knows
// the routes and fields from contracts B7 v2 (lead-calls, generate-summary,
// summary_source/status), B3 v2 (recording_status/reason) and B4 v1.2
// (lead-prefill summary). If the backend lands with a different path or field
// name, change it here and in tests/unit/leadDetail/leadDeskCalls.test.jsx.
import { normalizeUnifiedCall } from '../../../../pages/dashboard/callLogs/callLog.normalize';
import { callsBase, detailUrl } from '../../../../pages/dashboard/callLogs/callLogQuery';
import { OUTCOME } from '../../../../pages/dashboard/callLogs/callLog.constants';

export const DESK_CALL_URLS = {
    // Newest first, at most 50, Elevate desk-phone (voice:) rows only. Call Logs view.
    forLead: (practiceId, leadId) => `${callsBase(practiceId)}lead-calls/?lead=${encodeURIComponent(leadId)}`,
    // Call Logs manage. Never charged twice (already_done / in_progress).
    generateSummary: (practiceId, callId) => `${detailUrl(practiceId, callId)}generate-summary/`,
    // Call Logs view. `summary` = what the call summary/transcript said.
    leadPrefill: (practiceId, callId) => `${detailUrl(practiceId, callId)}lead-prefill/`,
};

const str = (v) => (typeof v === 'string' ? v.trim() : '');

export const SUMMARY_SOURCE = {
    elevate: 'Summary by Elevate',
    dentalsem: 'Summary by DentalSEM',
};

// One Call Logs row (same normaliser as the Call Logs page) plus the B3/B7 fields.
export const deskCallOf = (raw) => ({
    ...normalizeUnifiedCall(raw),
    summarySource: SUMMARY_SOURCE[raw?.summary_source] ? raw.summary_source : '',
    summaryStatus: str(raw?.summary_status) || 'none',
    transcriptStatus: str(raw?.transcript_status) || 'none',
    recordingStatus: str(raw?.recording_status) || 'none',
    recordingReason: str(raw?.recording_reason),
    topics: Array.isArray(raw?.ai_topics) ? raw.ai_topics.filter((t) => typeof t === 'string' && t.trim()) : [],
});

export const deskCallsOf = (data) => (Array.isArray(data?.results) ? data.results : [])
    .filter((raw) => raw && String(raw.id || '').startsWith('voice:'))
    .map(deskCallOf);

// `desk_phone_connected` (CU-869fnwcr5): the practice has an enabled desk
// phone (Elevate) connection. Only a literal true counts, so an older backend
// without the field reads as "no desk phone".
export const deskPhoneConnectedOf = (data) => data?.desk_phone_connected === true;

// What to say about a call's summary when there is no summary text.
// `canGenerate` is decided separately (manage permission + eligibility).
export const summaryStatusText = (call) => {
    switch (call.summaryStatus) {
        case 'available': return 'Summary ready: open the call to read it.';
        case 'waiting_for_elevate': return 'Waiting for the Elevate summary (usually within 15 minutes of the call).';
        case 'queued': return 'Summary on its way. Refresh in a minute or two.';
        case 'not_available': return 'No summary for this call yet.';
        default: return call.outcome === OUTCOME.MISSED ? 'No summary: the call was not answered.' : 'No summary for this call.';
    }
};

// B3 recording_reason → plain words (status "unavailable").
const RECORDING_REASONS = {
    line_not_set_up: 'Recording not fetched: this phone line isn\'t set up yet. Ask a practice admin to set it up in Settings → Integrations.',
    older_than_30_days: 'Recording no longer available: the phone system keeps recordings for 30 days.',
    not_recorded_ring_group: 'Not recorded: answered through a ring group with no recorded desk.',
    not_answered: 'No recording: the call was not answered.',
    not_found: 'No recording found at the phone system.',
    recordings_off: 'Recording access isn\'t switched on for this phone system.',
    desk_recording_off: 'Not recorded: this desk\'s recording is off at Elevate',
};
export const recordingText = (call) => {
    if (call.hasRecording) return '';
    if (call.recordingStatus === 'fetching') return 'Recording is being fetched from the phone system.';
    if (call.recordingStatus === 'unavailable') return RECORDING_REASONS[call.recordingReason] || 'Recording not available.';
    return '';
};

// Generate is offered only where the backend would accept it (B7 §2): a
// manager, an answered desk-phone call with a recording, and no summary
// already available or on its way.
export const canOfferGenerate = (call, canManage) => Boolean(canManage)
    && call.summaryStatus === 'not_available'
    && call.outcome === OUTCOME.ANSWERED
    && call.hasRecording;

// B7 §2 replies → { tone: 'info' | 'success' | 'error', message, nextStatus }.
const GENERATE_409 = {
    own_ai_off: 'Summaries by DentalSEM are switched off for this practice, so none can be generated here.',
    not_eligible: 'This call can\'t be summarised (test call, disconnected line, or not this practice\'s call).',
};
const GENERATE_400 = {
    no_recording: 'There is no recording to summarise.',
    not_answered: 'The call was not answered, so there is nothing to summarise.',
    not_a_desk_phone_call: 'Only desk-phone calls can be summarised here.',
};
export const generateOutcome = (response) => {
    if (response?.ok) {
        const { state, summary } = response.data || {};
        if (state === 'already_done' && summary === 'none') return { tone: 'info', message: 'The call was transcribed but nobody spoke, so there is no summary.', nextStatus: 'none' };
        if (state === 'already_done') return { tone: 'success', message: 'This call already has a summary. Refreshing.', nextStatus: 'available', refresh: true };
        if (state === 'in_progress') return { tone: 'info', message: 'A summary is already being made for this call.', nextStatus: 'queued' };
        return { tone: 'success', message: 'Summary on its way. It usually takes a minute or two.', nextStatus: 'queued' };
    }
    const reason = response?.data?.reason;
    if (response?.status === 409) return { tone: 'error', message: GENERATE_409[reason] || response.data?.detail || 'A summary can\'t be generated for this call.', nextStatus: 'none' };
    if (response?.status === 400) return { tone: 'error', message: GENERATE_400[reason] || response.data?.detail || 'A summary can\'t be generated for this call.', nextStatus: 'none' };
    if (response?.status === 403) return { tone: 'error', message: 'You need Call Logs manage access to generate a summary.' };
    // status 0 = no reply: the request may have been accepted. The backend
    // never charges twice, so a later press is safe; say so rather than "failed".
    if (response?.status === 0) return { tone: 'info', message: 'No reply from the server. The summary may already be on its way: refresh in a minute before trying again.' };
    return { tone: 'error', message: response?.data?.detail || 'Could not start the summary. Try again later.' };
};

// B4 §6 `summary` → labelled rows with where each came from. Exported so the
// Create lead form (F4) can show the same source tags.
export const PREFILL_SOURCE = {
    elevate_summary: 'from Elevate summary',
    dentalsem_summary: 'from DentalSEM summary',
    transcript: 'from the transcript',
};
const PREFILL_FIELDS = [
    ['name', 'Name'],
    ['treatment_interest', 'Treatment interest'],
    ['callback_time', 'Call back'],
];
export const prefillRows = (summary) => {
    const source = PREFILL_SOURCE[summary?.source] || '';
    if (!summary || !source) return [];
    return PREFILL_FIELDS
        .filter(([key]) => str(summary[key]))
        .map(([key, label]) => ({ key, label, value: str(summary[key]), source }));
};
